import { state, saveToLocalStorage, getActiveSettings, countedRoundsFor } from '../state.js';
import { formatPlayerName, escapeHtml, playerBirthYear } from '../utils/formatting.js';
import { resolveDisplayRating, highestKnownRating } from '../utils/player-ratings.js';
import * as PlayerDB from '../services/player-db.js';
import { callFunction, currentLang } from '../services/functions-api.js';
import { updateUIState, liveRegistrationIdsForPlayer } from '../app.js';
import { renderTeams } from './teams.js';
import { syncRequestedByesButton } from './byes.js';
import { refreshPlayerStatsIfOpen } from './player-stats.js';
import { nsfPlayerBadgeHtml, wireNsfBadges } from './nsf-membership-ui.js';
import { ensureNsfCheck, onNsfCheckChange } from '../services/nsf-membership.js';
import { applyColumnVisibility } from './column-views.js';

// An NSF check answering, or NSF's details put on a player, redraws the roster.
onNsfCheckChange(() => renderPlayerList());

// DOM Elements
const playersList = document.getElementById('players-list');
const playerSearch = document.getElementById('player-search');
const addPlayerModal = document.getElementById('add-player-modal');
const btnSavePlayer = document.getElementById('btn-save-player');


// The "toggle all" button says what the click will do: if anyone is absent the
// click marks everyone present, otherwise it marks everyone absent. Same rule as
// the click handler in app.js, so the label never lies about the next action.
function syncToggleAllStatusLabel() {
    const btn = document.getElementById('btn-toggle-all-status');
    if (!btn) return;
    const anyAbsent = state.players.some(p => !p.status);
    btn.innerHTML = `<i class="ri-checkbox-multiple-circle-line"></i> ${anyAbsent ? tr('Set All Present') : tr('Set All Absent')}`;
}

/**
 * The rounds this player has asked to sit out, from the next one onwards, as a badge
 * beside their presence.
 *
 * Without it a requested bye is invisible until the round is paired and the player turns
 * up on the list against BYE — by which point "why was Hansen not paired?" is a question
 * asked in the playing hall rather than answered on the roster.
 */
function byeBadgeHtml(player) {
    const upcoming = window.ByeRequests.upcoming(player, state.rounds.length + 1);
    if (upcoming.length === 0) return '';
    const parts = upcoming.map(e => `${e.round} (${e.value === 'half' ? '½' : '0'})`).join(', ');
    return `<span class="status-badge upcoming" style="margin-left: 0.35rem; padding: 0.15rem 0.5rem; font-size: 0.7rem;" title="${escapeHtml(tr('Sitting out round {rounds}', { rounds: parts }))}"><i class="ri-calendar-schedule-line"></i> ${escapeHtml(parts)}</span>`;
}

export function renderPlayerList() {
    // The teams panel lives on this page and is driven by the same roster, so it is
    // refreshed here — a player added, removed or renamed changes what the squads show.
    // Ahead of the early return, because the panel has its own container.
    renderTeams();

    // The Requested Byes button belongs to this page and only applies to some formats and
    // not to spectators, so it is kept in step with whatever is open.
    syncRequestedByesButton();

    // The Statistics window counts this same roster, so an open one follows it.
    refreshPlayerStatsIfOpen();

    // Which columns show depends on who is looking (js/ui/column-views.js).
    applyColumnVisibility();

    if (!playersList) return;

    syncToggleAllStatusLabel();

    const query = (playerSearch ? playerSearch.value : '').toLowerCase();

    // Spectators get a club / nation / rating-range filter instead of FIDE-DB search.
    if (state.isSpectatorMode) populateSpectatorPlayerFilters();

    // Filter players
    let filtered = state.players.filter(p => {
        if (query) {
            const fullName = (p.lastName + " " + p.givenName).toLowerCase();
            if (!fullName.includes(query)) return false;
        }
        if (state.isSpectatorMode && !passesSpectatorFilters(p)) return false;
        return true;
    });

    // Sort players
    const sort = state.playerSort;
    filtered.sort((a, b) => {
        let valA = a[sort.column];
        let valB = b[sort.column];

        // Special handling for numeric columns
        if (sort.column === 'rating' || sort.column === 'startNo' || sort.column === 'birthYear') {
            valA = parseInt(valA) || 0;
            valB = parseInt(valB) || 0;
        } else if (sort.column === 'status') {
            valA = a.status ? 1 : 0;
            valB = b.status ? 1 : 0;
        } else {
            valA = (valA || '').toString().toLowerCase();
            valB = (valB || '').toString().toLowerCase();
        }

        if (valA < valB) return sort.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sort.direction === 'asc' ? 1 : -1;
        return 0;
    });

    if (state.players.length === 0) {
        playersList.innerHTML = `<tr><td colspan="10" style="text-align: center; padding: 3rem; color: var(--text-muted);">${tr('No players registered yet. Click "Add Player" to get started.')}</td></tr>`;
        return;
    }

    if (filtered.length === 0) {
        const msg = query
            ? tr('No players matching "{query}".', { query: escapeHtml(query) })
            : tr('No players match the current filters.');
        playersList.innerHTML = `<tr><td colspan="10" style="text-align: center; padding: 3rem; color: var(--text-muted);">${msg}</td></tr>`;
        return;
    }

    const hasMultipleGroups = state.activeTournament && state.activeTournament.groups && state.activeTournament.groups.length > 1;

    playersList.innerHTML = filtered.map((player) => {
        const moveBtnHTML = hasMultipleGroups ? `<button class="btn-icon-small btn-move-player" data-id="${escapeHtml(player.id)}" title="${tr('Move to another group')}" style="color: var(--primary);"><i class="ri-arrow-right-line"></i></button>` : '';
        return `
        <tr>
            <td>${player.startNo || '-'}</td>
            <td><div class="tbl-title clickable-player" data-id="${escapeHtml(player.id)}" style="cursor: pointer; color: var(--primary);">${escapeHtml(player.givenName)}</div></td>
            <td><div class="tbl-title clickable-player" data-id="${escapeHtml(player.id)}" style="cursor: pointer; color: var(--primary);">${escapeHtml(player.lastName)}</div></td>
            <td class="col-title"><span class="status-badge upcoming" style="font-size: 0.7rem;">${escapeHtml(player.title || '-')}</span></td>
            <td class="col-class">${escapeHtml(player.class || '-')}</td>
            <td class="col-club">${escapeHtml(player.club || '-')}</td>
            <td class="col-rating">
                <strong title="${escapeHtml(getPlayerDisplayRating(player).tooltip)}">${escapeHtml((player.rating && player.rating !== 0 && player.rating !== '0') ? player.rating : '-')}</strong>
            </td>
            <td class="col-birthyear">${escapeHtml(playerBirthYear(player) || '-')}</td>
            <td><span class="status-badge ${player.status ? 'active' : 'completed'} ${state.isSpectatorMode ? '' : 'btn-toggle-status'}" data-id="${escapeHtml(player.id)}" style="${state.isSpectatorMode ? '' : 'cursor: pointer;'} padding: 0.15rem 0.5rem; font-size: 0.7rem;" title="${state.isSpectatorMode ? '' : tr('Toggle Presence')}">${player.status ? tr('Present') : tr('Absent')}</span>${byeBadgeHtml(player)}${nsfPlayerBadgeHtml(player)}</td>
            <td>
                <div style="display: ${state.isSpectatorMode ? 'none' : 'flex'}; gap: 0.5rem;">
                    ${moveBtnHTML}
                    <button class="btn-icon-small btn-edit-player" data-id="${escapeHtml(player.id)}" title="${tr('Edit Player')}"><i class="ri-edit-line"></i></button>
                    <button class="btn-icon-small btn-player-to-db" data-id="${escapeHtml(player.id)}" title="${tr('Add to Database')}"><i class="ri-database-2-line"></i></button>
                    <button class="btn-icon-small btn-icon-danger btn-delete-player" data-id="${escapeHtml(player.id)}" title="${tr('Remove Player')}"><i class="ri-delete-bin-line"></i></button>
                </div>
            </td>
        </tr>
        `;
    }).join('');

    // Attach event listeners to new elements
    document.querySelectorAll('.btn-delete-player').forEach(btn => btn.addEventListener('click', () => deletePlayer(btn.dataset.id)));
    document.querySelectorAll('.btn-toggle-status').forEach(btn => btn.addEventListener('click', () => togglePlayerStatus(btn.dataset.id)));
    document.querySelectorAll('.btn-edit-player').forEach(btn => btn.addEventListener('click', () => openEditPlayerModal(btn.dataset.id)));
    document.querySelectorAll('.clickable-player').forEach(el => el.addEventListener('click', () => openPlayerProfile(el.dataset.id)));
    document.querySelectorAll('.btn-move-player').forEach(btn => btn.addEventListener('click', () => openMovePlayerModal(btn.dataset.id)));
    document.querySelectorAll('.btn-player-to-db').forEach(btn => btn.addEventListener('click', () => {
        const player = state.players.find(p => String(p.id) === String(btn.dataset.id));
        if (player) openAddToDatabaseModal([player]);
    }));
    wireNsfBadges(playersList);

    // A group that requires NSF membership and has no answer yet (just opened, or the
    // switch just turned on) is checked now; the badges above say "checking" meanwhile.
    ensureNsfCheck();
}

// ----- spectator player filters (club / nation / rating range) -----

function distinctValues(arr) {
    return [...new Set(arr.map(v => (v || '').trim()).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
}

function fillFilterSelect(sel, values, allLabel) {
    if (!sel) return;
    const prev = sel.value;
    sel.innerHTML = [`<option value="">${allLabel}</option>`]
        .concat(values.map(v => `<option value="${escapeHtml(v)}">${escapeHtml(v)}</option>`))
        .join('');
    // Preserve the current choice if it still exists, else fall back to "all".
    sel.value = values.includes(prev) ? prev : '';
}

function populateSpectatorPlayerFilters() {
    fillFilterSelect(document.getElementById('filter-player-club'),
        distinctValues(state.players.map(p => p.club)), tr('All clubs'));
    fillFilterSelect(document.getElementById('filter-player-fed'),
        distinctValues(state.players.map(p => p.fed)), tr('All nations'));
}

function passesSpectatorFilters(p) {
    const clubEl = document.getElementById('filter-player-club');
    const fedEl = document.getElementById('filter-player-fed');
    const minEl = document.getElementById('filter-rating-min');
    const maxEl = document.getElementById('filter-rating-max');
    const club = clubEl ? clubEl.value : '';
    const fed = fedEl ? fedEl.value : '';
    const minR = minEl && minEl.value !== '' ? parseInt(minEl.value) : null;
    const maxR = maxEl && maxEl.value !== '' ? parseInt(maxEl.value) : null;

    if (club && (p.club || '').trim() !== club) return false;
    if (fed && (p.fed || '').trim() !== fed) return false;
    if (minR !== null || maxR !== null) {
        const r = getPlayerDisplayRating(p).rating || 0;
        if (minR !== null && r < minR) return false;
        if (maxR !== null && r > maxR) return false;
    }
    return true;
}

export function handlePlayerSort(column) {
    if (state.playerSort.column === column) {
        state.playerSort.direction = state.playerSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
        state.playerSort.column = column;
        state.playerSort.direction = 'asc';
    }

    // Update header UI
    const headers = document.querySelectorAll('#player-table-header th.sortable');
    headers.forEach(th => {
        const col = th.dataset.sort;
        th.classList.remove('active');
        const icon = th.querySelector('i');
        if (icon) icon.className = 'ri-arrow-up-down-line';

        if (col === state.playerSort.column) {
            th.classList.add('active');
            if (icon) {
                icon.className = state.playerSort.direction === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc';
            }
        }
    });

    renderPlayerList();
}

/**
 * Remove a player from the tournament — and, when they got there by registering
 * online, withdraw the registration behind them.
 *
 * WHY THE REGISTRATION HAS TO GO TOO. It is the same person recorded twice: once
 * on the arbiter's player list, once as an entry the registrant can see. Deleting
 * only the player left the entry alive, and with it a live "Unregister" button on
 * a tournament they were no longer in — so the removal was invisible to the one
 * person it concerned, right up until they arrived to play. Worse, the entry was
 * still a registration: it counted, it could be re-imported, and it kept the
 * Register button hidden so they could not even enter again.
 *
 * WHICH REGISTRATIONS GO: every one that IS this player, not only the one whose
 * id they happen to carry. Auto-import claims a registration by FIDE id and by
 * name as well as by id (see liveRegistrationIdsForPlayer), so an entry can be
 * standing behind a player without ever having stamped its id onto them — a
 * second submission, or someone the arbiter had already typed in by hand before
 * they registered. Withdrawing only the carried id left those alive, and the next
 * snapshot imported the player straight back: removed, told they were removed,
 * and there again on the next refresh.
 *
 * The withdrawal goes through the server (see withdrawRegistration in
 * functions/index.js), which is what sends the notification and the email.
 * A failure there does NOT block the removal: the arbiter asked for this player
 * to be off the list, and the list is theirs. They are told what did not happen.
 */
export async function deletePlayer(id) {
    const player = state.players.find(p => String(p.id) === String(id));
    const registrationIds = liveRegistrationIdsForPlayer(player);

    const question = registrationIds.length
        ? tr('Remove this player from the tournament? Their online registration is withdrawn as well, and they are told they have been removed.')
        : tr('Remove this player from the tournament?');

    const confirmed = await window.Dialog.confirm(question, tr('Remove Player'), tr('Remove'), true);
    if (!confirmed) return;

    state.players = state.players.filter(p => String(p.id) !== String(id));
    saveToLocalStorage();
    renderPlayerList();
    updateUIState();

    if (registrationIds.length === 0) return;

    const results = await Promise.all(registrationIds.map(withdrawRegistrationForArbiter));
    // 'not_found' means it was already gone — the outcome the arbiter wanted.
    const failed = results.filter(r => !r.ok && r.error !== 'not_found');
    if (failed.length > 0) {
        await window.Dialog.warn(
            tr('{name} was removed from the player list, but their online registration could not be withdrawn, so they have not been told. Try again from the registrations panel.',
                { name: player ? formatPlayerName(player, false) : tr('The player') }),
            tr('Registration Still Open')
        );
    }
}

/**
 * Withdraw a registration as an arbiter, through the server.
 *
 * Every arbiter-initiated removal goes through here — deleting the player above,
 * and rejecting a pending entry from the registrations panel in app.js — so that
 * the person on the other end is told exactly once, the same way, whichever
 * screen the arbiter happened to use. `authApp.cancelRegistration` is the OTHER
 * path: it is what a registrant uses to withdraw themselves, and it stays silent
 * because nobody needs telling about their own action.
 *
 * Resolves { ok, error?, notified? } and never throws.
 */
export async function withdrawRegistrationForArbiter(registrationId) {
    return callFunction('withdrawRegistration', { registrationId, lang: currentLang() });
}

export function togglePlayerStatus(id) {
    if (state.isSpectatorMode) return;
    const player = state.players.find(p => p.id === id);
    if (player) {
        player.status = !player.status;
        saveToLocalStorage();
        renderPlayerList();
    }
}

export function openEditPlayerModal(id) {
    const player = state.players.find(p => p.id === id);
    if (player) {
        state.editingPlayerId = id;
        addPlayerModal.querySelector('h2').textContent = tr('Edit Player');
        if (btnSavePlayer) btnSavePlayer.textContent = tr('Save');

        document.getElementById('player-start-no').value = player.startNo || '';
        document.getElementById('player-class').value = player.class || '';
        document.getElementById('player-title').value = player.title || '';
        document.getElementById('player-given-name').value = player.givenName || '';
        document.getElementById('player-last-name').value = player.lastName || '';
        document.getElementById('player-club').value = player.club || '';
        document.getElementById('player-fed').value = player.fed || '';
        document.getElementById('player-birthyear').value = player.birthYear || '';
        if (document.getElementById('player-birthdate')) document.getElementById('player-birthdate').value = player.birthDate || '';
        document.getElementById('player-fide-id').value = player.fideId || '';
        document.getElementById('player-nsf-id').value = player.nsfId || '';
        document.getElementById('player-rating').value = player.rating || '';
        document.getElementById('player-k-factor').value = player.kFactor || '';
        document.getElementById('player-status').checked = player.status !== false;
        
        // Fill hidden ratings if they exist
        if (document.getElementById('player-std-rating')) document.getElementById('player-std-rating').value = player.standardRating || 0;
        if (document.getElementById('player-rapid-rating')) document.getElementById('player-rapid-rating').value = player.rapidRating || 0;
        if (document.getElementById('player-blitz-rating')) document.getElementById('player-blitz-rating').value = player.blitzRating || 0;
        // This player's own database link, or none. Left as it was, the field held
        // whatever the last search pick wrote there, and saving the edit moved the
        // player onto that record (or dropped their link when it was empty).
        if (document.getElementById('player-db-player-id')) document.getElementById('player-db-player-id').value = player.dbPlayerId || '';

        addPlayerModal.classList.add('active');
    }
}

// The rating to show for a player under the ACTIVE group's settings. The
// resolution itself is pure and lives in js/utils/player-ratings.js (so it can be
// tested without a DOM); only the translated tooltip is built here.
export function getPlayerDisplayRating(player) {
    const settings = getActiveSettings();
    const resolved = resolveDisplayRating(player, settings);

    // A custom club list is identified by an opaque id, which would make a
    // useless tooltip — prefer the list's name when the settings carry it.
    const isCustomList = settings.ratingListId && resolved.source === settings.ratingListId;
    const sourceLabel = isCustomList && settings.ratingListName
        ? settings.ratingListName
        : resolved.source.charAt(0).toUpperCase() + resolved.source.slice(1);

    const tooltip = resolved.isFallback
        ? tr('Fallback rating from {source}', { source: sourceLabel })
        : tr('Official {source} rating', { source: sourceLabel });

    return { ...resolved, tooltip };
}

export function openPlayerProfile(playerId) {
    if (!playerId || playerId === 'WO' || playerId === 'BYE') return;
    const player = state.players.find(p => String(p.id) === String(playerId));
    if (!player) return;

    const modal = document.getElementById('player-profile-modal');
    if (!modal) return;

    const isAlreadyOpen = modal.classList.contains('active');

    if (isAlreadyOpen) {
        modal.classList.add('profile-transitioning');
        setTimeout(() => {
            updateProfileDOM(player, playerId);
            modal.classList.remove('profile-transitioning');
        }, 150);
    } else {
        updateProfileDOM(player, playerId);
        modal.classList.add('active');
    }
}

/*
 * The details only the arbiter sees: the full date of birth, sex, phone number and
 * email address.
 *
 * These have always been stored — typed into Add Player, or sent in through the
 * registration form when the organizer asked for them — and until now the profile
 * showed none of them. An organizer who put "Date of birth: required" on their
 * form had no way to read a single answer.
 *
 * A row appears only when there is something in it, so a hand-entered player does
 * not grow four empty lines. `arbiter-only` in the markup is what keeps them from a
 * spectator, who can open this same profile from the standings; the display value
 * set here cannot override it, because that rule is `!important`.
 */
function renderArbiterOnlyDetails(player) {
    const rows = [
        // Private like the contact details since the NSF membership check: the roster's
        // public half never carries it (js/utils/player-privacy.js).
        ['profile-nsf-item', 'profile-nsf', player.nsfId || ''],
        ['profile-birthdate-item', 'profile-birthdate', trDate(player.birthDate || '')],
        ['profile-sex-item', 'profile-sex', sexLabel(player.sex)],
        ['profile-phone-item', 'profile-phone', player.phone || ''],
        ['profile-email-item', 'profile-email', player.email || '']
    ];
    rows.forEach(([itemId, valueId, value]) => {
        const item = document.getElementById(itemId);
        const el = document.getElementById(valueId);
        if (!item || !el) return;
        el.textContent = value || '-';
        item.style.display = value ? 'flex' : 'none';
    });
}

function sexLabel(sex) {
    const s = String(sex || '').trim().toLowerCase().charAt(0);
    if (s === 'm') return tr('Male');
    if (s === 'f') return tr('Female');
    return '';
}

// Show what the player wrote when they registered: the free-text comment and the
// answers to the organizer's own extra questions (t-shirt size, accommodation, …).
// These arrive via the registration link / API and are carried onto the player at
// import; before this they were stored but had nowhere to be read.
function renderRegistrationAnswers(player) {
    const section = document.getElementById('profile-registration-answers');
    const list = document.getElementById('profile-registration-answers-list');
    if (!section || !list) return;

    const rows = [];
    if (player.registrationComment) {
        rows.push([tr('Comment'), player.registrationComment]);
    }
    // Keys are the machine keys the organizer's questions were saved under; the
    // label the registrant actually saw lives on the tournament config, so fall
    // back to the key itself rather than inventing a label here.
    const answers = player.registrationAnswers;
    if (answers && typeof answers === 'object') {
        Object.keys(answers).forEach(key => {
            const value = answers[key];
            if (value === '' || value === null || value === undefined) return;
            rows.push([key, value === 'true' ? tr('Yes') : (value === 'false' ? tr('No') : value)]);
        });
    }

    if (rows.length === 0) {
        section.style.display = 'none';
        list.innerHTML = '';
        return;
    }

    section.style.display = 'block';
    list.innerHTML = rows.map(([label, value]) => `
        <div style="display: flex; justify-content: space-between; gap: 1rem; font-size: 0.85rem;">
            <span style="color: var(--text-muted); flex-shrink: 0;">${escapeHtml(label)}</span>
            <span style="color: var(--text-main); text-align: right; overflow-wrap: anywhere;">${escapeHtml(String(value))}</span>
        </div>`).join('');
}

function updateProfileDOM(player, playerId) {
    const initials = (player.givenName ? player.givenName[0] : '') + (player.lastName ? player.lastName[0] : '');
    document.getElementById('profile-initials').textContent = initials || '?';
    document.getElementById('profile-name').textContent = formatPlayerName(player);

    let badgesHTML = '';
    if (player.title) badgesHTML += `<span class="status-badge upcoming">${escapeHtml(player.title)}</span>`;
    if (player.rating) badgesHTML += `<span class="status-badge active">${tr('Rating')}: ${escapeHtml(player.rating)}</span>`;
    if (player.class) badgesHTML += `<span class="status-badge completed">${tr('Class')}: ${escapeHtml(player.class)}</span>`;
    document.getElementById('profile-badges').innerHTML = badgesHTML;

    document.getElementById('profile-snr').textContent = player.startNo || '-';
    document.getElementById('profile-club').textContent = player.club || '-';
    document.getElementById('profile-fed').textContent = player.fed || '-';

    document.getElementById('profile-birthyear').textContent = playerBirthYear(player) || '-';

    const fideLink = player.fideId ? `<a href="https://ratings.fide.com/profile/${encodeURIComponent(player.fideId)}" target="_blank" style="color: var(--primary); text-decoration: none; font-weight: 600;">${escapeHtml(player.fideId)}</a>` : '-';
    document.getElementById('profile-fide').innerHTML = fideLink;

    renderArbiterOnlyDetails(player);
    renderRegistrationAnswers(player);

    const pParts = (getActiveSettings().pointSystem || '1-0.5-0').split('-').map(Number);
    const pWin = pParts[0];
    const pDraw = pParts[1];
    const pLoss = pParts[2] || 0;

    let points = 0;
    let games = 0;
    let opponentsHistory = [];

    state.rounds.forEach((round, rIdx) => {
        const pairing = round.pairings.find(p => String(p.white) === String(playerId) || String(p.black) === String(playerId));
        if (pairing) {
            const isWhite = String(pairing.white) === String(playerId);
            const oppId = isWhite ? pairing.black : pairing.white;
            let oppName = 'BYE / WO';
            let oppRating = '-';
            if (oppId && oppId !== 'WO' && oppId !== 'BYE') {
                const oppPlayer = state.players.find(p => String(p.id) === String(oppId));
                oppName = oppPlayer ? formatPlayerName(oppPlayer, true) : tr('Unknown');
                oppRating = oppPlayer ? (oppPlayer.rating || 'UR') : '-';
            }

            let resStr = '-';
            if (pairing.result) {
                games++;
                if (pairing.result === '1-0') {
                    resStr = isWhite ? '<span style="color: var(--success); font-weight: bold;">1</span>' : '<span style="color: var(--danger); font-weight: bold;">0</span>';
                    points += isWhite ? pWin : pLoss;
                } else if (pairing.result === '0-1') {
                    resStr = !isWhite ? '<span style="color: var(--success); font-weight: bold;">1</span>' : '<span style="color: var(--danger); font-weight: bold;">0</span>';
                    points += !isWhite ? pWin : pLoss;
                } else if (pairing.result === '0.5-0.5') {
                    resStr = '<span style="color: var(--warning); font-weight: bold;">½</span>';
                    points += pDraw;
                } else if (pairing.result === '+/-') {
                    // A forfeit loss scores 0, never pLoss (that is for a game lost over the board).
                    resStr = isWhite ? '<span style="color: var(--success); font-weight: bold;">+</span>' : '<span style="color: var(--danger); font-weight: bold;">-</span>';
                    points += isWhite ? pWin : 0;
                } else if (pairing.result === '-/+') {
                    resStr = !isWhite ? '<span style="color: var(--success); font-weight: bold;">+</span>' : '<span style="color: var(--danger); font-weight: bold;">-</span>';
                    points += !isWhite ? pWin : 0;
                } else if (pairing.result === '-/-') {
                    // Double forfeit: both lost by forfeit, nobody scores.
                    resStr = '<span style="color: var(--danger); font-weight: bold;">-</span>';
                }
            }

            let gameRatingChange = '-';
            if (player.rating && player.rating > 0 && oppId && oppId !== 'WO' && oppId !== 'BYE') {
                const oppPlayer = state.players.find(p => String(p.id) === String(oppId));
                if (oppPlayer && oppPlayer.rating && parseInt(oppPlayer.rating) > 0) {
                    const isForfeit = pairing.result === '+/-' || pairing.result === '-/+' || pairing.result === '-/-';
                    if (pairing.result && !isForfeit) {
                        let score = 0;
                        if (pairing.result === '1-0') score = isWhite ? 1 : 0;
                        else if (pairing.result === '0-1') score = isWhite ? 0 : 1;
                        else if (pairing.result === '0.5-0.5') score = 0.5;

                        let playerRating = parseInt(player.rating);
                        let oppRating = parseInt(oppPlayer.rating);
                        let dr = oppRating - playerRating;
                        if (playerRating < 2650) {
                            if (dr > 400) dr = 400;
                            if (dr < -400) dr = -400;
                        }
                        const pd = SwissEngine.getPD(dr);
                        const k = SwissEngine.calculateK(player);
                        const change = (score - pd) * k;
                        
                        const val = change.toFixed(1).replace('.', ',');
                        const sign = change > 0 ? '+' : '';
                        const color = change > 0 ? 'var(--success)' : (change < 0 ? 'var(--danger)' : 'var(--text-main)');
                        gameRatingChange = `<span style="color: ${color}; font-weight: 600;">${sign}${val}</span>`;
                    }
                }
            }

            opponentsHistory.push(`
                <tr>
                    <td>${round.roundNo}</td>
                    <td>${isWhite ? `<div style="width: 14px; height: 14px; border-radius: 50%; background: #ffffff; border: 2px solid #cbd5e1; margin: auto;" title="${tr('White')}"></div>` : `<div style="width: 14px; height: 14px; border-radius: 50%; background: #1e293b; border: 2px solid #94a3b8; margin: auto;" title="${tr('Black')}"></div>`}</td>
                    <td class="${oppId && oppId !== 'WO' && oppId !== 'BYE' ? 'clickable-player' : ''}" data-id="${escapeHtml(oppId)}" style="white-space: normal; word-break: break-word; ${oppId && oppId !== 'WO' && oppId !== 'BYE' ? 'cursor: pointer; color: var(--primary); font-weight: 500;' : ''}">${oppName}</td>
                    <td style="text-align: center; color: var(--text-muted);">${oppRating}</td>
                    <td style="text-align: center;">${resStr}</td>
                    <td class="rating-calc-only" style="text-align: center;">${gameRatingChange}</td>
                </tr>
            `);
        }
    });

    let rank = '-';
    let aor = '-';
    let performance = '-';
    let ratingChange = '-';
    let kFactor = '-';

    if (state.rounds.length > 0) {
        // Same rating rules as the standings column, or the two would disagree
        // about the same player. Null (config not loaded yet) falls back to FIDE.
        const settings = getActiveSettings();
        const ratingCfg = PlayerDB.getCachedListConfig(settings.ratingDbId, settings.ratingListId);
        // The tournament's own tie-breaks, not the engine's default set: the rank shown
        // here has to be the rank on the standings screen, and passing `undefined` ranked
        // every tournament by Buchholz whatever it was configured with.
        const isRR = ['rr', 'drr', 'round-robin', 'roundrobin', 'double-rr', 'double-round-robin'].includes(settings.format);
        const standings = SwissEngine.calculateStandings(
            state.players, state.rounds, settings.pointSystem, settings.tiebreaks, ratingCfg,
            {
                totalRounds: parseInt(settings.roundsCount) || state.rounds.length,
                isRoundRobin: isRR,
                countedRounds: countedRoundsFor(settings)
            }
        );
        const pData = standings.find(p => String(p.id) === String(playerId));
        if (pData) {
            rank = standings.indexOf(pData) + 1;
            aor = pData.aor ? Math.round(pData.aor) : '-';
            performance = pData.pr ? Math.round(pData.pr) : '-';
            kFactor = pData.k || '-';
            
            if (pData.ratingChange && pData.ratingChange !== 0) {
                const val = pData.ratingChange.toFixed(1).replace('.', ',');
                const sign = pData.ratingChange > 0 ? '+' : '';
                const color = pData.ratingChange > 0 ? 'var(--success)' : 'var(--danger)';
                ratingChange = `<span style="color: ${color};">${sign}${val}</span>`;
            }
        }
    } else {
        const s = getActiveSettings();
        kFactor = SwissEngine.calculateK(player, PlayerDB.getCachedListConfig(s.ratingDbId, s.ratingListId)) || '-';
    }

    if (!player.rating || parseInt(player.rating) === 0) {
        kFactor = '-';
    }

    document.getElementById('profile-points').textContent = points;
    document.getElementById('profile-games').textContent = games;
    document.getElementById('profile-rank').textContent = rank;
    document.getElementById('profile-aor').textContent = aor;
    document.getElementById('profile-performance').textContent = performance;
    document.getElementById('profile-ratingchange').innerHTML = ratingChange;
    document.getElementById('profile-k').textContent = kFactor;
    document.getElementById('profile-snr').textContent = player.startNo || '-';

    const gamesList = document.getElementById('profile-games-list');
    if (opponentsHistory.length === 0) {
        gamesList.innerHTML = '<tr><td colspan="4" class="text-center" style="padding: 1.5rem; color: var(--text-muted);">No games played yet.</td></tr>';
    } else {
        gamesList.innerHTML = opponentsHistory.join('');
    }
}

// Module-level variable for moving player
let movingPlayerId = null;

export function openMovePlayerModal(playerId) {
    if (state.rounds.length >= 2) {
        window.Dialog.warn(tr('You cannot move a player to another group once the second round is paired.'), tr('Move Blocked'));
        return;
    }

    const player = state.players.find(p => String(p.id) === String(playerId));
    if (!player) return;

    movingPlayerId = playerId;

    const promptEl = document.getElementById('move-player-prompt');
    if (promptEl) {
        promptEl.textContent = tr('Select the group to move {name} to:', { name: formatPlayerName(player) });
    }

    const select = document.getElementById('move-player-target-group');
    if (select && state.activeTournament && state.activeTournament.groups) {
        const otherGroups = state.activeTournament.groups.filter(g => String(g.id) !== String(state.activeGroupId));
        select.innerHTML = otherGroups.map(g => `<option value="${escapeHtml(g.id)}">${escapeHtml(g.name)}</option>`).join('');
    }

    const modal = document.getElementById('move-player-modal');
    if (modal) {
        modal.classList.add('active');
    }
}

async function confirmMovePlayer() {
    const playerId = movingPlayerId;
    if (!playerId) return;

    const select = document.getElementById('move-player-target-group');
    if (!select) return;

    const targetGroupId = select.value;
    if (!targetGroupId) return;

    const playerIndex = state.players.findIndex(p => String(p.id) === String(playerId));
    if (playerIndex === -1) return;

    const player = state.players[playerIndex];

    const targetGroup = state.activeTournament.groups.find(g => String(g.id) === String(targetGroupId));
    if (!targetGroup) return;

    // Remove from current group's players
    state.players.splice(playerIndex, 1);

    // Initialize target group arrays if not present
    if (!targetGroup.players) targetGroup.players = [];
    if (!targetGroup.rounds) targetGroup.rounds = [];

    // Add to target group's players
    targetGroup.players.push(player);

    // Save
    saveToLocalStorage();

    // Close Modal
    const modal = document.getElementById('move-player-modal');
    if (modal) {
        modal.classList.remove('active');
    }

    // Refresh UI
    renderPlayerList();
    updateUIState();

    window.Dialog.success(tr('Moved {name} successfully to {group}.', { name: formatPlayerName(player), group: targetGroup.name }), tr('Player Moved'));
}

// Bind move confirmation once DOM is loaded / ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        const btn = document.getElementById('btn-confirm-move-player');
        if (btn) btn.addEventListener('click', confirmMovePlayer);
    });
} else {
    const btn = document.getElementById('btn-confirm-move-player');
    if (btn) btn.addEventListener('click', confirmMovePlayer);
}

// ---------------------------------------------------------------------------
// Copying tournament players into one of the arbiter's player databases
//
// The Players tab is where an arbiter first meets a player, so it is where they
// decide to keep one. Both entry points — the whole roster, and the per-row
// action — open the same picker: which database, and which of its rating lists
// the player's current tournament rating should become the opening rating in.
// The write is PlayerDB.createPlayer, the same call the Player Databases tab
// makes, so there is one code path for creating a database record.
// ---------------------------------------------------------------------------

let dbCandidates = [];        // the tournament players being copied
let selectableDatabases = []; // this arbiter's databases, with their lists

const $ = (id) => document.getElementById(id);

export async function openAddToDatabaseModal(players) {
    const candidates = (players || []).filter(Boolean);
    if (candidates.length === 0) {
        window.Dialog.alert(tr('There are no players to add yet.'), tr('Add to Database'));
        return;
    }

    try {
        selectableDatabases = (await PlayerDB.listMyDatabases()).filter(d => d.role !== 'pending');
    } catch (e) {
        console.error('Could not load player databases:', e);
        selectableDatabases = [];
    }

    if (selectableDatabases.length === 0) {
        window.Dialog.alert(
            tr('You have no player databases yet. Create one under Player Databases first.'),
            tr('Add to Database')
        );
        return;
    }

    dbCandidates = candidates;

    $('player-to-db-subtitle').textContent = candidates.length === 1
        ? tr('Add {name} to one of your player databases.', { name: formatPlayerName(candidates[0]) })
        : tr('Add all {count} players in this group to one of your player databases.', { count: candidates.length });

    const dbSelect = $('player-to-db-database');
    dbSelect.innerHTML = selectableDatabases
        .map(d => `<option value="${escapeHtml(d.id)}">${escapeHtml(d.name || tr('Untitled'))}</option>`)
        .join('');

    // Preselect the database this group is already bound to, if it is one of
    // theirs — that is almost always the one they mean.
    const bound = getActiveSettings().ratingDbId;
    if (bound && selectableDatabases.some(d => d.id === bound)) dbSelect.value = bound;

    syncAddToDatabaseLists();
    $('player-to-db-result').innerHTML = '';
    $('btn-player-to-db-run').removeAttribute('disabled');
    $('player-to-db-modal').classList.add('active');
}

// The rating-list picker follows the chosen database, and preselects the list
// the active group reports to when that list lives in the chosen database.
function syncAddToDatabaseLists() {
    const dbId = $('player-to-db-database').value;
    const database = selectableDatabases.find(d => String(d.id) === String(dbId));
    const lists = Array.isArray(database && database.lists) ? database.lists : [];

    // A database with no rating lists takes the players and rates nothing, so the
    // picker — which would offer a single "no rating" option — goes away entirely.
    const listGroup = $('player-to-db-list-group');
    if (listGroup) listGroup.style.display = lists.length > 0 ? '' : 'none';

    const listSelect = $('player-to-db-list');
    listSelect.innerHTML = `<option value="">${escapeHtml(tr('No rating (leave unrated)'))}</option>` +
        lists.map(l => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name || l.id)}</option>`).join('');

    const settings = getActiveSettings();
    if (settings.ratingDbId === dbId && settings.ratingListId
        && lists.some(l => l.id === settings.ratingListId)) {
        listSelect.value = settings.ratingListId;
    }
}

async function runAddToDatabase() {
    const dbId = $('player-to-db-database').value;
    if (!dbId || dbCandidates.length === 0) return;

    const database = selectableDatabases.find(d => String(d.id) === String(dbId));
    const listId = $('player-to-db-list').value;
    const result = $('player-to-db-result');
    const runBtn = $('btn-player-to-db-run');

    runBtn.setAttribute('disabled', 'disabled');
    result.innerHTML = `<p style="color: var(--text-muted);">${escapeHtml(tr('Adding...'))}</p>`;

    // Skip anyone the database already holds, so running this twice (or on a
    // roster that grew) tops the database up instead of duplicating it. Matched
    // the same way the report preview matches: FIDE id first, then name.
    let existing = [];
    try {
        existing = await PlayerDB.syncPlayers(dbId);
    } catch (e) {
        console.error('Could not read the target database:', e);
    }
    const takenNames = new Set(existing.map(p => (p.name || '').toLowerCase()).filter(Boolean));
    const takenFideIds = new Set(existing.filter(p => p.fideId).map(p => String(p.fideId)));

    // A player with no rating of their own opens at the list's start rating, the
    // same as one added by hand — matching js/ui/player-db-page.js's import.
    const listCfg = listId
        ? window.RatingEngine.normalizeListConfig(PlayerDB.findRatingList(database, listId) || {})
        : null;

    // Only worth linking the tournament player back to the new record when this
    // group actually reports to this database; a dbPlayerId pointing into some
    // other database would be a lie the report preview would then act on.
    const linkBack = getActiveSettings().ratingDbId === dbId;

    let added = 0, skipped = 0;
    for (const p of dbCandidates) {
        const name = PlayerDB.formatDbPlayerName(p.lastName, p.givenName).toLowerCase();
        const fideId = p.fideId ? String(p.fideId) : '';
        if (!name || takenNames.has(name) || (fideId && takenFideIds.has(fideId))) { skipped++; continue; }
        takenNames.add(name);
        if (fideId) takenFideIds.add(fideId);

        const ratings = {};
        if (listCfg) {
            // The highest rating they hold anywhere, not just the one this
            // tournament happens to show: a club list opening a player at their
            // rapid rating because the event is rapid would rate them below
            // their strength from their very first game here.
            const own = highestKnownRating(p);
            const value = own > 0 ? own : listCfg.startRating;
            if (value > 0) ratings[listId] = { value, games: 0, updatedAt: Date.now() };
        }

        try {
            const created = await PlayerDB.createPlayer(dbId, {
                lastName: p.lastName || '',
                givenName: p.givenName || '',
                title: p.title || '',
                club: p.club || '',
                fed: p.fed || '',
                birthYear: p.birthYear || '',
                fideId: p.fideId || '',
                nsfId: p.nsfId || '',
                ratings
            });
            if (linkBack && created) p.dbPlayerId = created.id;
            added++;
        } catch (e) {
            console.error('Could not add player to the database:', p, e);
            skipped++;
        }
    }

    // The card on the Player Databases tab shows a count. Cosmetic, so a failed
    // update is not worth surfacing.
    if (added > 0 && database) {
        const next = Math.max(0, (database.playerCount || 0) + added);
        database.playerCount = next;
        PlayerDB.updateDatabase(dbId, { playerCount: next }).catch(() => {});
        if (linkBack) saveToLocalStorage();
    }

    runBtn.removeAttribute('disabled');
    result.innerHTML = `<p style="color: var(--success);">${
        escapeHtml(tr('Added {added} players ({skipped} already there).', { added, skipped }))}</p>`;
}

// Wires the two entry points and the picker. Called once from app.js at startup.
export function initPlayerDatabaseActions() {
    $('btn-players-to-database')?.addEventListener('click', () => openAddToDatabaseModal(state.players));
    $('player-to-db-database')?.addEventListener('change', syncAddToDatabaseLists);
    $('btn-player-to-db-run')?.addEventListener('click', runAddToDatabase);
}
